"use client";

import * as LabelPrimitive from "@radix-ui/react-label";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../lib/utils";
import { Button } from "./button";
import { Icon, IconComponent } from "./icon";
import { Tooltip } from "./tooltip";

/**
 * The `peer-disabled` rules only fire where the label is a sibling of its
 * control. A label sitting above its control — or grouped with it inside a
 * `<fieldset disabled>` that locks a whole section — is not a peer, so the
 * `fieldset:disabled` rules dim it there too and a disabled section reads as
 * disabled throughout rather than as bright labels over greyed-out inputs.
 *
 * At `opacity-50` to match every control in the library, not the `opacity-70`
 * of the `peer-disabled` rule beside it: a whole section at two opacities reads
 * as a rendering fault rather than as locked.
 */
const labelVariants = cva(
  "flex w-fit items-center gap-1 text-sm font-medium leading-tight peer-disabled:cursor-not-allowed peer-disabled:opacity-70 [&[for]]:cursor-pointer [fieldset:disabled_&]:cursor-not-allowed [fieldset:disabled_&]:opacity-50",
);

interface LabelProps
  extends
    React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>,
    VariantProps<typeof labelVariants> {
  asterisk?: boolean;
  tooltipContent?: string;
  tooltipIcon?: IconComponent;
}

/**
 * Radix UI (API reference): https://www.radix-ui.com/docs/primitives/components/label#api-reference
 *
 * shadcn/ui docs: https://ui.shadcn.com/docs/components/label
 *
 * Clicking on a label should focus the associated input:
 *
 * ```jsx
 * <Label htmlFor="name">Name</Label>
 * <input id="name" />
 * ```
 */
const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  LabelProps
>(
  (
    { children, className, asterisk, tooltipContent, tooltipIcon, ...props },
    ref,
  ) => (
    <LabelPrimitive.Root
      className={cn(labelVariants(), className)}
      ref={ref}
      {...props}
    >
      {children}
      {asterisk && <span className="text-destructive">*</span>}

      {tooltipContent && (
        <Tooltip content={tooltipContent}>
          <Button
            icon={tooltipIcon || <Icon icon="info" />}
            size="content"
            variant="transparent"
          />
        </Tooltip>
      )}
    </LabelPrimitive.Root>
  ),
);
Label.displayName = LabelPrimitive.Root.displayName;

export { Label, labelVariants, type LabelProps };
